import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Add as AddIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ManageAccounts as UserManagementIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  AdminEmptyState,
  AdminDialog,
  AdminFilterSummary,
  AdminTableActions,
  AdminTablePanel,
} from "@/components/common";
import { PageShell } from "@/components/common/PageShell";
import { LoadingRing } from "@/components/common/LoadingRing";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import StoreMultiSelect from "@/components/common/StoreMultiSelect";
import { useAuth } from "@/contexts/AuthContext";
import { useLocalizedTablePagination } from "@/hooks/useLocalizedTablePagination";
import { StoreService } from "@/services/features/StoreService";
import { UserService } from "@/services/features/UserService";
import { paths } from "@/routes/paths";
import { usePermission } from "@/utils/permissionManager";
import { getRoleDisplayName } from "@/utils/roleManager";
import { formatStoreSelectorLabel } from "@/utils/store";
import {
  formatRelativeStoreTime,
  formatStoreDateTimeWithTimezone,
  getBrowserTimezone,
} from "@/utils/storeTime";

interface User {
  id: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  active: boolean;
  lastLoggedIn: number;
  archived: boolean;
  allowedStores: number[];
  customerId: number;
}

interface Customer {
  id: number;
  name: string;
  live: boolean;
}

interface Store {
  id: number;
  name: string;
  customerId: number;
  live: boolean;
  timezone?: string;
}
const UserManagement = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const userService = UserService.getInstance();
  const storeService = StoreService.getInstance();
  const { user, logout } = useAuth();
  const { isCraveUser } = usePermission(user?.role || "");
  const isCraveUserValue = React.useMemo(() => isCraveUser(), [isCraveUser]);

  // State declarations
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  // Legacy single-select states (kept for backward compatibility within the file)
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  // Add flag to track if selections were initialized from URL
  const [initializedFromUrl, setInitializedFromUrl] = useState(false);
  // Prevent duplicate fetches (e.g., StrictMode double effects or redundant URL hydration)
  const lastFetchKeyRef = React.useRef<string | null>(null);

  // Multi-select state to mirror Store Comparison behavior
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<number[]>([]); // empty => All Customers
  const [groups, setGroups] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[] | null>(null); // null/empty => All Groups
  // Legacy single group for backward-compatibility with existing effects
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedStoresMulti, setSelectedStoresMulti] = useState<Store[]>([]);

  // Stores filtered by selected customers (if any)
  const customerStores = React.useMemo(() => {
    if (!selectedCustomerIds || selectedCustomerIds.length === 0) return stores;
    const idSet = new Set(selectedCustomerIds);
    return stores.filter((s) => idSet.has((s as any).customerId as number));
  }, [stores, selectedCustomerIds]);

  // Stores further filtered by selected groups (if any)
  const filteredStores = React.useMemo(() => {
    const base = customerStores;
    if (!selectedGroups || selectedGroups.length === 0) return base;
    const grp = new Set(selectedGroups);
    return base.filter((s) => grp.has(((s as any).group as string | undefined) || ""));
  }, [customerStores, selectedGroups]);

  // Count stores per group within current customer scope
  const groupCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const s of customerStores) {
      const key = ((s as any).group as string | undefined) || "";
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }, [customerStores]);

  // Default to first store when filteredStores updates and no store is selected
  useEffect(() => {
    if (isLoadingStores) return;
    if (!filteredStores || filteredStores.length === 0) return;
    if (selectedStore) return;
    const first = filteredStores[0];
    setSelectedStore(first);
    setSelectedStoresMulti([first]);
    const params = new URLSearchParams(searchParams);
    params.set("storeId", String(first.id));
    params.delete("storeIds");
    setSearchParams(params, { replace: true });
  }, [filteredStores, selectedStore, isLoadingStores, searchParams, setSearchParams]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const localizedTablePagination = useLocalizedTablePagination();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Pagination state for large user lists
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Sorting state
  const [orderBy, setOrderBy] = useState<
    "name" | "role" | "lastLogin" | "username" | "email" | null
  >(null);
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  const handleRequestSort = (property: "name" | "role" | "lastLogin" | "username" | "email") => {
    if (orderBy === property) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(property);
      setOrder("asc");
    }
    setPage(0);
  };

  // Filter users by search query
  const filteredUsers = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.firstName || ""} ${u.lastName || ""}`.toLowerCase();
      const username = (u.username || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const role = (getRoleDisplayName(u.role) || "").toLowerCase();
      return name.includes(q) || username.includes(q) || email.includes(q) || role.includes(q);
    });
  }, [users, searchQuery]);

  const sortedUsers = React.useMemo(() => {
    const base = filteredUsers;
    if (!orderBy) return base;

    const collator = new Intl.Collator(undefined, {
      numeric: true,
      sensitivity: "base",
    });

    const sorted = [...base].sort((a, b) => {
      if (orderBy === "lastLogin") {
        const av = a.lastLoggedIn ?? 0;
        const bv = b.lastLoggedIn ?? 0;
        // Place missing/0 lastLoggedIn at the end for asc, at start for desc
        const aMissing = !av;
        const bMissing = !bv;
        if (aMissing && bMissing) return 0;
        if (aMissing) return order === "asc" ? 1 : -1;
        if (bMissing) return order === "asc" ? -1 : 1;
        return order === "asc" ? av - bv : bv - av;
      } else {
        const av =
          orderBy === "name"
            ? `${a.lastName || ""} ${a.firstName || ""}`.trim()
            : orderBy === "username"
              ? a.username || ""
              : orderBy === "email"
                ? a.email || ""
                : getRoleDisplayName(a.role) || "";
        const bv =
          orderBy === "name"
            ? `${b.lastName || ""} ${b.firstName || ""}`.trim()
            : orderBy === "username"
              ? b.username || ""
              : orderBy === "email"
                ? b.email || ""
                : getRoleDisplayName(b.role) || "";
        const cmp = collator.compare(av, bv);
        return order === "asc" ? cmp : -cmp;
      }
    });

    return sorted;
  }, [filteredUsers, orderBy, order]);

  // Simplified fetch users function - moved to top before it's used in effect dependencies
  const fetchUsers = React.useCallback(
    async (customerId?: number | null, storeId?: number | null) => {
      // Require at least one filter (store or customer)
      if (!storeId && !customerId) return;

      setIsLoadingUsers(true);
      setError(null);

      try {
        const params: any = { includeArchived: false };
        if (customerId) params.customerId = customerId;
        if (storeId) params.storeId = storeId;

        const response = await userService.getUsers(params);

        if (response?.data) {
          setUsers(response.data);
        }
      } catch (error) {
        // Handle authentication errors
        if (
          axios.isAxiosError(error) &&
          (error.response?.status === 401 || error.response?.status === 403)
        ) {
          setError("Your session has expired. Please log in again.");
          // Optionally logout the user to force re-authentication
          setTimeout(() => logout(), 3000);
        } else {
          setError(error instanceof Error ? error.message : "Failed to fetch users");
        }

        setUsers([]);
      } finally {
        setIsLoadingUsers(false);
      }
    },
    [userService, logout],
  );

  // Modified update URL parameters function
  // Update URL without triggering additional re-renders
  const updateUrlParams = React.useCallback(
    (customer: Customer | null, store: Store | null) => {
      // Get current parameters
      const currentParams = new URLSearchParams(searchParams);
      const currentCustomerId = currentParams.get("customerId");
      const currentStoreId = currentParams.get("storeId");

      // Check if there are actual changes to avoid unnecessary updates
      const newCustomerId = customer?.id.toString() || null;
      const newStoreId = store?.id.toString() || null;

      // Only update if values are different
      if (
        (currentCustomerId !== newCustomerId && (currentCustomerId || newCustomerId)) ||
        (currentStoreId !== newStoreId && (currentStoreId || newStoreId))
      ) {
        const params = new URLSearchParams();

        if (customer) {
          params.set("customerId", customer.id.toString());
        }

        if (store) {
          params.set("storeId", store.id.toString());
        }

        // Use replaceState to avoid adding browser history entries
        setSearchParams(params, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  // Load users whenever store selection changes (single-store only)
  useEffect(() => {
    const activeStoreId = selectedStore?.id;
    if (!activeStoreId) return;

    const key = `${selectedCustomer?.id ?? "null"}:${activeStoreId}`;
    if (lastFetchKeyRef.current === key) {
      return; // prevent duplicate fetches for the same selection
    }
    lastFetchKeyRef.current = key;

    fetchUsers(selectedCustomer?.id ?? null, activeStoreId);
  }, [selectedCustomer, selectedStore, fetchUsers]);

  // Modify the selection handlers to ensure they work with URL parameters
  const handleStoreSelect = React.useCallback(
    (newStore: Store | null) => {
      // Skip if no change to prevent unnecessary re-renders
      if (selectedStore?.id === newStore?.id) {
        return;
      }

      setSelectedStore(newStore);
      setUsers([]); // Clear users immediately for better UX

      // If non-Crave user (customer admin), update URL params directly here
      // This prevents circular update issues for customer admins who only see store filter
      if (!isCraveUserValue && selectedCustomer && newStore) {
        updateUrlParams(selectedCustomer, newStore);
      }

      // Do not fetch here; rely on the selectedStore effect to perform a single fetch
      // when selection settles to avoid duplicate requests.
      // if (newStore) {
      //   fetchUsers(selectedCustomer?.id ?? null, newStore.id);
      // }
    },
    [selectedCustomer, selectedStore, fetchUsers, isCraveUserValue, updateUrlParams],
  );
  // Effect to handle initial URL parameters when directly accessing the page with parameters
  useEffect(() => {
    if (!isLoadingInitial || initializedFromUrl) return;

    const loadFromUrlOnInitialRender = async () => {
      const customerIdParam = searchParams.get("customerId");
      const storeIdParam = searchParams.get("storeId");
      const storeIdsParam = searchParams.get("storeIds");
      const groupParam = searchParams.get("group");

      try {
        // Determine customer context
        let customerToSet: Customer | null = null;
        if (isCraveUserValue) {
          if (customerIdParam) {
            const customerId = parseInt(customerIdParam);
            if (!isNaN(customerId)) {
              if (customers.length === 0) {
                const response = await storeService.getCustomers();
                if (response.data) {
                  setCustomers(response.data);
                  customerToSet = response.data.find((c) => c.id === customerId) || null;
                }
              } else {
                customerToSet = customers.find((c) => c.id === customerId) || null;
              }
            }
          }
        } else if (user?.customerId) {
          // For non‑Crave users, always scope to their customer
          customerToSet = {
            id: user.customerId,
            name: user.customerName || "",
            live: true,
          };
        }

        if (customerToSet) {
          setSelectedCustomer(customerToSet);
          // hydrate multi-select ids for customers when applicable
          if (isCraveUserValue && customerIdParam) {
            const cid = parseInt(customerIdParam);
            if (!isNaN(cid)) {
              setSelectedCustomerIds([cid]);
            }
          }
        } else {
          // If no specific customer in URL and Crave user, default to All (empty selection)
          if (isCraveUserValue) {
            setSelectedCustomerIds([]);
          }
        }

        // Load stores dataset depending on context
        let storesData: Store[] = [];
        setIsLoadingStores(true);
        try {
          if (isCraveUserValue && customerToSet) {
            const storesResponse = await storeService.getStoresByCustomer(customerToSet.id);
            if (storesResponse.data) {
              storesData = storesResponse.data;
              setStores(storesData);
            }
          } else {
            const storesResponse = await storeService.getStores();
            if (storesResponse.data) {
              storesData = storesResponse.data;
              setStores(storesData);
            }
          }
        } finally {
          setIsLoadingStores(false);
        }

        // Apply group param if present ('' means Ungrouped)
        if (groupParam !== null) {
          setSelectedGroups([groupParam]);
        }

        // If store IDs are in URL, select the first one (backward compatibility)
        if (storeIdsParam) {
          const ids = storeIdsParam
            .split(",")
            .map((v) => parseInt(v.trim()))
            .filter((n) => !isNaN(n));
          if (ids.length) {
            const firstStore = storesData.find((s) => s.id === ids[0]) || null;
            setSelectedStore(firstStore ?? null);
            setSelectedStoresMulti(firstStore ? [firstStore] : []);
            // Normalize URL to single storeId
            const params = new URLSearchParams(searchParams);
            if (firstStore) {
              params.set("storeId", String(firstStore.id));
            } else {
              params.delete("storeId");
            }
            params.delete("storeIds");
            setSearchParams(params, { replace: true });
          }
        }

        // If single store ID is in URL, select it (backward compatibility)
        if (storeIdParam) {
          const storeId = parseInt(storeIdParam);
          if (!isNaN(storeId)) {
            const storeToSet = storesData.find((s) => s.id === storeId) || null;
            if (storeToSet) {
              setSelectedStore(storeToSet);
              setSelectedStoresMulti([storeToSet]);
              // Rely on selectedStore effect to fetch users; avoid duplicate fetch here
            }
          }
        }

        // If no store provided, default to the first store
        if (!storeIdParam && !storeIdsParam && storesData.length > 0) {
          const first = storesData[0];
          setSelectedStore(first);
          setSelectedStoresMulti([first]);
          const params = new URLSearchParams(searchParams);
          params.set("storeId", String(first.id));
          params.delete("storeIds");
          setSearchParams(params, { replace: true });
        }

        // Mark as initialized from URL to prevent rerendering loops
        setInitializedFromUrl(true);
      } catch (error) {
        console.error("Failed to initialize from URL parameters", error);
      }
    };

    loadFromUrlOnInitialRender();
  }, [
    isLoadingInitial,
    initializedFromUrl,
    searchParams,
    customers,
    isCraveUserValue,
    user,
    storeService,
    fetchUsers,
  ]);

  // Effect to sync UI state to URL when user makes selections
  useEffect(() => {
    // Skip during initial loading or if selection is from URL initialization
    // Also skip for customer admins (non-Crave users) as they manage URL updates directly
    if (isLoadingInitial || !initializedFromUrl || !isCraveUserValue) return;
    // If multi-store selection is in URL, skip legacy single store URL sync
    if (searchParams.has("storeIds")) return;

    // Add debounce for Crave users to prevent rapid URL updates
    const timeoutId = setTimeout(() => {
      const customerIdParam = searchParams.get("customerId");
      const storeIdParam = searchParams.get("storeId");

      const isCustomerMatchingUrl = !selectedCustomer
        ? !customerIdParam
        : selectedCustomer.id.toString() === customerIdParam;
      const isStoreMatchingUrl = !selectedStore
        ? !storeIdParam
        : selectedStore.id.toString() === storeIdParam;

      // Only update URL if our selections don't match what's already in the URL
      if (!isCustomerMatchingUrl || !isStoreMatchingUrl) {
        updateUrlParams(selectedCustomer, selectedStore);
      }
    }, 300); // Debounce URL updates by 300ms

    return () => clearTimeout(timeoutId);
  }, [
    selectedCustomer,
    selectedStore,
    searchParams,
    isLoadingInitial,
    initializedFromUrl,
    updateUrlParams,
    isCraveUserValue,
  ]);

  // Effect for fetching stores when customer changes
  useEffect(() => {
    if (!selectedCustomer?.id) return;

    const fetchStores = async () => {
      try {
        setIsLoadingStores(true);
        setError(null);

        if (isCraveUserValue) {
          const response = await storeService.getStoresByCustomer(selectedCustomer.id);
          if (response.data) {
            setStores(response.data);
          }
        } else {
          // For non-Crave users
          const response = await storeService.getStores();
          if (response.data) {
            setStores(response.data);
          }
        }
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : t("settings.userManagement.errors.failedFetchStores"),
        );
        setStores([]);
      } finally {
        setIsLoadingStores(false);
      }
    };

    fetchStores();
  }, [selectedCustomer, isCraveUserValue, storeService, user]);

  // Recompute available groups whenever customer-scoped stores change
  useEffect(() => {
    const uniq = new Set<string>();
    for (const s of customerStores) {
      uniq.add(((s as any).group as string | undefined) || "");
    }
    const next = Array.from(uniq);
    // Sort with 'Ungrouped' (empty key) last
    next.sort((a, b) => {
      if (a === "" && b !== "") return 1;
      if (b === "" && a !== "") return -1;
      return a.localeCompare(b);
    });
    setGroups(next);
  }, [customerStores]);

  // Keep legacy single selectedGroup in sync with multi selection
  useEffect(() => {
    const single = selectedGroups && selectedGroups.length === 1 ? selectedGroups[0] : null;
    if (single !== selectedGroup) {
      setSelectedGroup(single);
    }
  }, [selectedGroups]);

  // When selected group changes, clear store if it no longer belongs
  useEffect(() => {
    if (!selectedStore) return;
    // If no group is selected, all stores are valid
    if (selectedGroup == null) return;
    const g = ((selectedStore as any).group as string | undefined) || "";
    if (g !== selectedGroup) {
      setSelectedStore(null);
      setUsers([]);
      const params = new URLSearchParams(searchParams);
      if (params.has("storeId")) {
        params.delete("storeId");
        setSearchParams(params, { replace: true });
      }
    }
  }, [selectedGroup, selectedStore, searchParams, setSearchParams]);

  // When group is selected/changed, auto-select the first store in the filtered list
  useEffect(() => {
    // Require a selected customer context and a selected group
    if (!selectedCustomer) return;
    if (selectedGroup == null) return;

    // Ensure we have filtered stores to choose from
    if (!filteredStores || filteredStores.length === 0) return;

    // If no store is selected or current store is not in the filtered list, pick the first
    const isCurrentValid =
      !!selectedStore && filteredStores.some((s) => s.id === (selectedStore as any).id);
    if (!isCurrentValid) {
      const first = filteredStores[0];
      handleStoreSelect(first);
    }
  }, [selectedGroup, filteredStores, selectedCustomer, selectedStore, handleStoreSelect]);

  // Keyboard shortcuts: '}' or Shift+ArrowDown for next store, '{' or Shift+ArrowUp for previous store
  // Mirrors behavior on Live Experience and Store Performance pages
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ignore when typing in inputs or editable elements
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const isEditable = !!(
        target &&
        (target.getAttribute("contenteditable") === "true" ||
          tag === "input" ||
          tag === "textarea" ||
          tag === "select")
      );
      if (isEditable) return;

      let action: "next" | "prev" | null = null;
      if (e.key === "}" || (e.shiftKey && e.key === "ArrowDown")) action = "next";
      else if (e.key === "{" || (e.shiftKey && e.key === "ArrowUp")) action = "prev";

      if (!action) return;
      if (!filteredStores || filteredStores.length === 0) return;
      if (!selectedStore) return;

      e.preventDefault();

      const currentIdx = filteredStores.findIndex((s) => s.id === (selectedStore as any).id);
      if (currentIdx === -1) return;

      let nextIdx = action === "next" ? currentIdx + 1 : currentIdx - 1;
      if (nextIdx >= filteredStores.length) nextIdx = 0;
      if (nextIdx < 0) nextIdx = filteredStores.length - 1;

      const nextStore = filteredStores[nextIdx];
      handleStoreSelect(nextStore);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [filteredStores, selectedStore, handleStoreSelect]);

  // Initial data fetching
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingInitial(true);
        setError(null);

        // Check if we have URL parameters that need to be handled
        const hasUrlParams =
          searchParams.has("customerId") ||
          searchParams.has("storeId") ||
          searchParams.has("group");

        if (!hasUrlParams) {
          // Only set default selections if no URL parameters
          if (!isCraveUserValue) {
            // For non-Crave users, get their customer directly
            if (user?.customerId) {
              const customerData = {
                id: user.customerId,
                name: user.customerName || "",
                live: true,
              };
              setCustomers([customerData]);
              setSelectedCustomer(customerData);

              // Get stores for this customer
              const storeResponse = await storeService.getStores();
              if (storeResponse.data) {
                setStores(storeResponse.data);
                if (storeResponse.data.length > 0) {
                  const firstStore = storeResponse.data[0];
                  setSelectedStore(firstStore);

                  // Rely on selectedStore effect to fetch users; avoid duplicate fetch here
                }
              }
            }
          } else {
            // For Crave users, fetch all customers and preload all stores so filters are hydrated on page load
            const customersResponse = await storeService.getCustomers();
            if (customersResponse.data) {
              setCustomers(customersResponse.data);
            }
            // Preload all stores to populate Stores and Groups dropdowns before a customer is selected
            const allStoresResponse = await storeService.getStores();
            if (allStoresResponse.data) {
              setStores(allStoresResponse.data);
            }
          }

          // Mark as initialized since we're not handling URL params
          setInitializedFromUrl(true);
        } else {
          // For Crave users, we need customers list for URL param initialization
          if (isCraveUserValue) {
            const customersResponse = await storeService.getCustomers();
            if (customersResponse.data) {
              setCustomers(customersResponse.data);
            }
            // Also preload all stores so the Stores and Groups dropdowns are usable immediately
            const allStoresResponse = await storeService.getStores();
            if (allStoresResponse.data) {
              setStores(allStoresResponse.data);
            }
          }
          // URL parameters will be handled by the initialization effect
        }
      } catch (error) {
        setError(
          error instanceof Error
            ? error.message
            : t("settings.userManagement.errors.failedFetchUserData"),
        );
      } finally {
        setIsLoadingInitial(false);
      }
    };

    fetchInitialData();
  }, [user, isCraveUserValue, fetchUsers, searchParams, storeService]);

  const handleAddUser = () => {
    void navigate(`${paths.utilities.userManagement}/new${location.search}`);
  };

  const handleEditUser = (userId: number) => {
    void navigate(`${paths.utilities.userManagement}/edit/${userId}${location.search}`);
  };

  const handleDeleteUser = (userId: number) => {
    const user = users.find((u) => u.id === userId);
    if (user) {
      setUserToDelete(user);
      setDeleteDialogOpen(true);
    }
  };

  // Reset pagination when the user dataset changes significantly
  useEffect(() => {
    setPage(0);
  }, [users, selectedStoresMulti, selectedGroups, selectedCustomerIds]);

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);

      await userService.archiveUser(userToDelete.id.toString());

      enqueueSnackbar(t("settings.userManagement.messages.userDeleted"), { variant: "success" });
      setDeleteDialogOpen(false);

      // Remove the deleted user from the list
      setUsers((prevUsers) => prevUsers.filter((user) => user.id !== userToDelete.id));

      // Reset states
      setUserToDelete(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete user");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeleteError(null);
  };
  // Format date helper
  const formatDate = (timestamp: number | null): string => {
    if (!timestamp) {
      return t("settings.userManagement.empty.neverLoggedIn");
    }
    return formatStoreDateTimeWithTimezone(
      timestamp,
      getBrowserTimezone(),
      i18n.resolvedLanguage || i18n.language,
    );
  };

  // Combined effect to handle store selection from URL when stores are loaded
  useEffect(() => {
    // Skip if still loading, no customer selected, or during initial URL load
    if (isLoadingInitial || !selectedCustomer || !initializedFromUrl) return;

    const storeIdParam = searchParams.get("storeId");

    // If no store ID in URL or store already selected, do nothing
    if (!storeIdParam) return;
    if (selectedStore?.id.toString() === storeIdParam) return;

    // Try to find the store in the loaded stores
    const storeId = parseInt(storeIdParam);
    if (isNaN(storeId)) return;

    const storeToSelect = stores.find((s) => s.id === storeId);
    if (storeToSelect) {
      setSelectedStore(storeToSelect);
    }
  }, [searchParams, stores, selectedCustomer, selectedStore, isLoadingInitial, initializedFromUrl]);

  return (
    <PageShell
      title={t("settings.tabs.userManagement")}
      gradientTitle
      icon={<UserManagementIcon />}
      titleAccessory={
        <LoadingRing loading={isLoadingInitial || isLoadingStores || isLoadingUsers} />
      }
      headerActions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
          disabled={!selectedStore || isLoadingInitial || isLoadingStores}
        >
          {t("settings.userManagement.addUser")}
        </Button>
      }
    >
      <div className="pt-0 pb-24">
        <AdminFilterSummary
          filter={
            <Box
              sx={{
                display: "flex",
                width: "100%",
                alignItems: "center",
                gap: 2,
                flexWrap: { xs: "wrap", lg: "nowrap" },
              }}
            >
              {/* Customer selector (multi-select) - only for Crave users */}
              {isCraveUserValue && (
                <Box
                  sx={{
                    width: { xs: "100%", sm: "auto" },
                    flex: { xs: "1 1 100%", sm: "1 1 0" },
                    minWidth: 0,
                  }}
                >
                  <StoreMultiSelect
                    className="w-full"
                    minDropdownWidth={350}
                    dropdownAlign="right"
                    options={customers}
                    value={customers.filter((c) => selectedCustomerIds.includes(c.id))}
                    onChange={(next) => {
                      const ids = next.map((c) => c.id);
                      setSelectedCustomerIds(ids);
                      // Keep legacy single state for internal logic
                      if (ids.length === 1) {
                        const c = customers.find((cc) => cc.id === ids[0]) || null;
                        setSelectedCustomer(c);
                      } else {
                        setSelectedCustomer(null);
                      }

                      // Compute all stores matching the new customer filter and current group filter
                      const idSet = new Set(ids);
                      const baseStores =
                        ids.length === 0
                          ? stores
                          : stores.filter((s) => idSet.has((s as any).customerId as number));
                      const storesForSelect =
                        !selectedGroups || selectedGroups.length === 0
                          ? baseStores
                          : baseStores.filter((s) =>
                              new Set(selectedGroups).has(
                                ((s as any).group as string | undefined) || "",
                              ),
                            );

                      // Auto-select the first filtered store (single-select) and sync legacy + URL
                      const first = storesForSelect[0] ?? null;
                      setSelectedStore(first);
                      setSelectedStoresMulti(first ? [first] : []);

                      const params = new URLSearchParams(searchParams);
                      if (ids.length === 1) {
                        params.set("customerId", String(ids[0]));
                      } else {
                        params.delete("customerId");
                      }
                      if (first) {
                        params.set("storeId", String(first.id));
                        params.delete("storeIds");
                      } else {
                        params.delete("storeId");
                        params.delete("storeIds");
                      }
                      // Clear users until fetch triggers
                      setUsers([]);
                      setSearchParams(params, { replace: true });
                    }}
                    label={t("settings.userManagement.fields.customers")}
                    getOptionLabel={(c) => c.name}
                    getOptionKey={(c) => c.id}
                    emptyText={t("settings.userManagement.empty.noCustomers")}
                  />
                </Box>
              )}

              {/* Group selector (multi-select) */}
              <Box
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  flex: { xs: "1 1 100%", sm: "1 1 0" },
                  minWidth: 0,
                }}
              >
                <StoreMultiSelect
                  className="w-full"
                  minDropdownWidth={350}
                  dropdownAlign="right"
                  rowHeight="default"
                  options={groups}
                  value={selectedGroups ?? []}
                  onChange={(next) => {
                    const arr = next;
                    // Keep legacy single for internal logic
                    setSelectedGroups(arr);

                    // Compute filtered stores for current customer filter and new groups
                    const baseStores =
                      selectedCustomerIds && selectedCustomerIds.length
                        ? stores.filter((s) =>
                            new Set(selectedCustomerIds).has((s as any).customerId as number),
                          )
                        : stores;
                    const storesForSelect =
                      arr.length === 0
                        ? baseStores
                        : baseStores.filter((s) =>
                            new Set(arr).has(((s as any).group as string | undefined) || ""),
                          );

                    // Auto-select the first filtered store (single-select) and sync legacy + URL
                    const first = storesForSelect[0] ?? null;
                    setSelectedStore(first);
                    setSelectedStoresMulti(first ? [first] : []);

                    const params = new URLSearchParams(searchParams);
                    if (arr.length === 1) {
                      if (arr[0] !== "") {
                        params.set("group", arr[0]);
                      } else {
                        params.set("group", "");
                      }
                    } else {
                      params.delete("group");
                    }
                    if (first) {
                      params.set("storeId", String(first.id));
                      params.delete("storeIds");
                    } else {
                      params.delete("storeId");
                      params.delete("storeIds");
                    }
                    setUsers([]);
                    setSearchParams(params, { replace: true });
                  }}
                  label={t("settings.userManagement.fields.groups")}
                  getOptionLabel={(g) =>
                    g === "" ? t("storeInsights.common.options.ungrouped") : g
                  }
                  getOptionKey={(g) => g}
                  getOptionSecondary={(g) => {
                    const n = groupCounts[g] ?? 0;
                    return t("storeInsights.common.storeCount", { count: n });
                  }}
                  emptyText={t("settings.userManagement.empty.noGroups")}
                />
              </Box>

              {/* Store selector (single-select) */}
              <Box
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  flex: { xs: "1 1 100%", sm: "1 1 0" },
                  minWidth: 0,
                }}
              >
                <StoreMultiSelect
                  className="w-full font-bold"
                  minDropdownWidth={350}
                  dropdownAlign="right"
                  options={filteredStores}
                  value={selectedStore ? [selectedStore] : []}
                  single
                  autoCommit
                  onChange={(next) => {
                    const first = next[0] ?? null;
                    setSelectedStore(first);
                    setSelectedStoresMulti(first ? [first] : []); // keep legacy state in sync
                    const params = new URLSearchParams(searchParams);
                    if (first) {
                      params.set("storeId", String(first.id));
                      params.delete("storeIds");
                    } else {
                      params.delete("storeId");
                      params.delete("storeIds");
                    }
                    setSearchParams(params, { replace: true });
                  }}
                  label={t("settings.userManagement.fields.stores")}
                  disabled={isLoadingStores}
                  getOptionLabel={(s) => formatStoreSelectorLabel(s, t, user?.role, "id-name")}
                  getOptionKey={(s) => (s as any).id as number}
                  emptyText={t("settings.userManagement.empty.noStoresFound")}
                />
              </Box>
            </Box>
          }
          count={users.length}
          countLabel={t("settings.userManagement.fields.totalUsers")}
          icon={<UserManagementIcon color="primary" sx={{ fontSize: 40 }} />}
          filterMdSize={9}
          summaryMdSize={3}
        />

        {/* Users List */}
        <AdminTablePanel
          paperSx={{ mt: { xs: 0.5, sm: 0 } }}
          header={
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                alignItems: { xs: "stretch", lg: "center" },
                justifyContent: "space-between",
                gap: 1.5,
                px: { xs: 1, sm: 2 },
                pt: { xs: 1, sm: 1.5 },
                pb: { xs: 1, sm: 1 },
              }}
            >
              <Box sx={{ flex: 1, minWidth: { xs: "100%", lg: 280 } }}>
                <TextField
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  placeholder={t("settings.userManagement.search.placeholder")}
                  label={t("settings.userManagement.search.label")}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label={t("common.clear")}
                          onClick={() => {
                            setSearchQuery("");
                            setPage(0);
                          }}
                          edge="end"
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  ml: { xs: 0, lg: "auto" },
                  width: { xs: "100%", lg: "auto" },
                  "& .MuiTablePagination-root": { p: 0, width: "100%" },
                }}
              >
                <TablePagination
                  component="div"
                  count={filteredUsers.length}
                  page={page}
                  onPageChange={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[25, 50, 100, 250]}
                  {...localizedTablePagination}
                />
              </Box>
            </Box>
          }
          footer={
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <TablePagination
                component="div"
                count={filteredUsers.length}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[25, 50, 100, 250]}
                {...localizedTablePagination}
              />
            </Box>
          }
          containerProps={{
            sx: {
              width: "100%",
              overflowX: "auto",
              "&::-webkit-scrollbar": {
                height: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                borderRadius: "3px",
                backgroundColor: "rgba(0,0,0,0.2)",
              },
            },
          }}
          tableProps={{ size: isMobile ? "small" : "medium" }}
          tableSx={{ minWidth: { xs: 400, sm: 650, lg: 900 } }}

          mobileContent={
            <Box
              sx={{
                minWidth: 0,
                "& .MuiTypography-root": {
                  maxWidth: "100%",
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                },
              }}
            >
              <RecordSortControl
                value={orderBy}
                direction={order}
                onSort={handleRequestSort}
                options={[
                  { value: "name" as const, label: t("settings.userManagement.columns.name") },
                  { value: "role" as const, label: t("settings.userManagement.columns.role") },
                  {
                    value: "lastLogin" as const,
                    label: t("settings.userManagement.columns.lastLogin"),
                  },
                  {
                    value: "username" as const,
                    label: t("settings.userManagement.fields.username"),
                  },
                  { value: "email" as const, label: t("settings.userManagement.columns.email") },
                ]}
              />
              <RecordList
                items={
                  rowsPerPage > 0
                    ? sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : sortedUsers
                }
                getItemId={(user) => user.id}
                loading={isLoadingUsers}
                resetKey={JSON.stringify([
                  orderBy,
                  order,
                  ...(rowsPerPage > 0
                    ? sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    : sortedUsers
                  ).map((user) => user.id),
                ])}
                renderTitle={(user) => {
                  return (
                    <>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: "primary.main",
                            color: "common.white",
                            width: { xs: 32, sm: 40 },
                            height: { xs: 32, sm: 40 },
                            fontSize: { xs: "0.875rem", sm: "1rem" },
                          }}
                        >
                          {user.firstName[0]}
                          {user.lastName[0]}
                        </Avatar>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                              maxWidth: {
                                xs: "120px",
                                sm: "200px",
                                md: "300px",
                              },
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={`${user.firstName} ${user.lastName}`}
                          >
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: { xs: "block", md: "none" },
                              color: "text.secondary",
                              fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                              maxWidth: "180px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={user.username}
                          >
                            {user.username}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: { xs: "block", md: "none" },
                              color: "text.secondary",
                              fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                              maxWidth: "180px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={user.email}
                          >
                            {user.email}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              display: { xs: "block", sm: "none" },
                              color: "text.secondary",
                              mt: 0.5,
                              fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                            }}
                          >
                            {getRoleDisplayName(user.role)}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  );
                }}
                renderSummary={(user) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("settings.userManagement.columns.role")}</>,
                          value: (
                            <>
                              <Chip
                                label={getRoleDisplayName(user.role)}
                                size="small"
                                color="default"
                                icon={
                                  user.role === "CRAVE_ADMIN" ? (
                                    <Box
                                      component="img"
                                      src="/images/icons/crave_icon.svg"
                                      alt=""
                                      aria-hidden="true"
                                      sx={{ width: 14, height: 14 }}
                                    />
                                  ) : undefined
                                }
                                sx={{ fontSize: { xs: "0.6875rem", sm: "0.75rem" } }}
                              />
                            </>
                          ),
                        },
                        {
                          label: <>{t("settings.userManagement.columns.lastLogin")}</>,
                          value: (
                            <>
                              {user.lastLoggedIn ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 0.25,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                      whiteSpace: "nowrap",
                                    }}
                                    title={formatDate(user.lastLoggedIn)}
                                  >
                                    {formatDate(user.lastLoggedIn)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatRelativeStoreTime(
                                      user.lastLoggedIn,
                                      getBrowserTimezone(),
                                      i18n.resolvedLanguage || i18n.language,
                                    )}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontStyle: "italic",
                                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                  }}
                                >
                                  {formatDate(user.lastLoggedIn)}
                                </Typography>
                              )}
                            </>
                          ),
                        },
                      ]}
                    />
                  );
                }}
                renderDetails={(user) => {
                  return (
                    <RecordFields
                      fields={[
                        {
                          label: <>{t("settings.userManagement.fields.username")}</>,
                          value: (
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: { md: "200px", lg: "300px" },
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                }}
                                title={user.username}
                              >
                                {user.username}
                              </Typography>
                            </>
                          ),
                        },
                        {
                          label: <>{t("settings.userManagement.columns.email")}</>,
                          value: (
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: { lg: "300px" },
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                }}
                                title={user.email}
                              >
                                {user.email}
                              </Typography>
                            </>
                          ),
                        },
                      ]}
                    />
                  );
                }}
                renderActions={(user) => (
                  <AdminTableActions
                    actions={[
                      {
                        icon: <EditIcon />,
                        hint: t("settings.userManagement.actions.edit"),
                        action: () => handleEditUser(user.id),
                      },
                      {
                        icon: <DeleteIcon />,
                        hint: t("settings.userManagement.actions.delete"),
                        action: () => handleDeleteUser(user.id),
                        color: "error",
                      },
                    ]}
                  />
                )}
                emptyState={
                  <AdminEmptyState
                    icon={<UserManagementIcon sx={{ fontSize: 40 }} />}
                    text={
                      selectedStore
                        ? t("settings.userManagement.empty.noUsers")
                        : t("settings.userManagement.empty.selectStoreToViewUsers")
                    }
                  />
                }
              />
            </Box>
          }
        >
          <TableHead>
            <TableRow>
              <SortableTableCell
                activeColumn={orderBy}
                column="name"
                direction={order}
                onSort={handleRequestSort}
                sx={{
                  whiteSpace: "nowrap",
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  width: { xs: "auto", sm: "25%", lg: "20%" },
                }}
              >
                {t("settings.userManagement.columns.name")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={orderBy}
                column="role"
                direction={order}
                onSort={handleRequestSort}
                sx={{
                  display: { xs: "none", sm: "table-cell" },
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  width: { sm: "15%" },
                }}
              >
                {t("settings.userManagement.columns.role")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={orderBy}
                column="lastLogin"
                direction={order}
                onSort={handleRequestSort}
                sx={{
                  display: { xs: "none", lg: "table-cell" },
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  width: { lg: "25%" },
                }}
              >
                {t("settings.userManagement.columns.lastLogin")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={orderBy}
                column="username"
                direction={order}
                onSort={handleRequestSort}
                sx={{
                  display: { xs: "none", md: "table-cell" },
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  width: { sm: "25%" },
                }}
              >
                {t("settings.userManagement.fields.username")}
              </SortableTableCell>
              <SortableTableCell
                activeColumn={orderBy}
                column="email"
                direction={order}
                onSort={handleRequestSort}
                sx={{
                  display: { xs: "none", lg: "table-cell" },
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  width: { lg: "25%" },
                }}
              >
                {t("settings.userManagement.columns.email")}
              </SortableTableCell>
              <TableCell
                sx={{
                  width: { xs: "90px", sm: "15%" },
                  py: { xs: 1.5, sm: 2 },
                  px: { xs: 1, sm: 2 },
                  textAlign: "end",
                }}
              >
                {t("settings.userManagement.columns.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingUsers ? (
              Array.from({ length: 3 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ py: { xs: 1, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor: "grey.100",
                          borderRadius: "50%",
                        }}
                      />
                      <Box>
                        <Box
                          sx={{
                            width: 120,
                            height: 16,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            mb: 1,
                          }}
                        />
                        <Box
                          sx={{
                            width: 80,
                            height: 12,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 160,
                        height: 16,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", lg: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 24,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 16,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", lg: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 16,
                        bgcolor: "grey.100",
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                      width: { xs: "90px", sm: "auto" },
                      textAlign: "end",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        gap: { xs: 0.5, sm: 1 },
                        justifyContent: "flex-end",
                        minWidth: { xs: "74px", sm: "auto" },
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                        }}
                      />
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "grey.100",
                          borderRadius: 1,
                        }}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: { xs: 3, sm: 4 } }}>
                  <AdminEmptyState
                    icon={<UserManagementIcon sx={{ fontSize: 40 }} />}
                    text={
                      selectedStore
                        ? t("settings.userManagement.empty.noUsers")
                        : t("settings.userManagement.empty.selectStoreToViewUsers")
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              (rowsPerPage > 0
                ? sortedUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : sortedUsers
              ).map((user) => (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => handleEditUser(user.id)}
                  sx={{
                    cursor: "pointer",
                    "&&:hover, &&:hover > .MuiTableCell-root": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <TableCell sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: "primary.main",
                          color: "common.white",
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 },
                          fontSize: { xs: "0.875rem", sm: "1rem" },
                        }}
                      >
                        {user.firstName[0]}
                        {user.lastName[0]}
                      </Avatar>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                            maxWidth: {
                              xs: "120px",
                              sm: "200px",
                              md: "300px",
                            },
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={`${user.firstName} ${user.lastName}`}
                        >
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: { xs: "block", md: "none" },
                            color: "text.secondary",
                            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={user.username}
                        >
                          {user.username}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: { xs: "block", md: "none" },
                            color: "text.secondary",
                            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                            maxWidth: "180px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={user.email}
                        >
                          {user.email}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: { xs: "block", sm: "none" },
                            color: "text.secondary",
                            mt: 0.5,
                            fontSize: { xs: "0.6875rem", sm: "0.75rem" },
                          }}
                        >
                          {getRoleDisplayName(user.role)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", sm: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Chip
                      label={getRoleDisplayName(user.role)}
                      size="small"
                      color="default"
                      icon={
                        user.role === "CRAVE_ADMIN" ? (
                          <Box
                            component="img"
                            src="/images/icons/crave_icon.svg"
                            alt=""
                            aria-hidden="true"
                            sx={{ width: 14, height: 14 }}
                          />
                        ) : undefined
                      }
                      sx={{ fontSize: { xs: "0.6875rem", sm: "0.75rem" } }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", lg: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    {user.lastLoggedIn ? (
                      <Box
                        sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            whiteSpace: "nowrap",
                          }}
                          title={formatDate(user.lastLoggedIn)}
                        >
                          {formatDate(user.lastLoggedIn)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeStoreTime(
                            user.lastLoggedIn,
                            getBrowserTimezone(),
                            i18n.resolvedLanguage || i18n.language,
                          )}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontStyle: "italic", fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
                      >
                        {formatDate(user.lastLoggedIn)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", md: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: { md: "200px", lg: "300px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                      title={user.username}
                    >
                      {user.username}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      display: { xs: "none", lg: "table-cell" },
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: { lg: "300px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                      title={user.email}
                    >
                      {user.email}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      py: { xs: 1, sm: 2 },
                      px: { xs: 1, sm: 2 },
                      textAlign: "end",
                    }}
                  >
                    <AdminTableActions
                      actions={[
                        {
                          icon: <EditIcon />,
                          hint: t("settings.userManagement.actions.edit"),
                          action: () => handleEditUser(user.id),
                        },
                        {
                          icon: <DeleteIcon />,
                          hint: t("settings.userManagement.actions.delete"),
                          action: () => handleDeleteUser(user.id),
                          color: "error",
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </AdminTablePanel>
      </div>

      {/* Delete Confirmation Dialog */}
      <AdminDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        title={t("settings.userManagement.dialogs.deleteUserTitle")}
        icon={<DeleteIcon />}
        error={deleteError}
        errorOperation="delete"
        errorEntity={t("common.entities.user")}
        cancelAction={{
          label: t("common.cancel"),
          onClick: handleDeleteCancel,
          disabled: isDeleting,
        }}
        primaryAction={{
          label: isDeleting
            ? t("settings.userManagement.actions.deleting")
            : t("settings.userManagement.actions.delete"),
          onClick: () => void handleDeleteConfirm(),
          color: "error",
          loading: isDeleting,
        }}
      >
        <Typography>
          {t("settings.userManagement.dialogs.deleteUserDescription", {
            name: `${userToDelete?.firstName ?? ""} ${userToDelete?.lastName ?? ""}`.trim(),
          })}
        </Typography>
      </AdminDialog>
    </PageShell>
  );
};

export default UserManagement;
